import axios from "axios";
import { PagedResult } from "../interface/iPagedResult";

const API_BASE =
    process.env.REACT_APP_API_URL?.replace(/\/$/, "") ?? "https://localhost:7080";

const RECIPES_URL = `${API_BASE}/api/recipe`;

export interface RecipeCreateDto {
    title: string;
    description?: string | null;
    categoryIds: number[];
    ingredients: string[];
    instructionsHtml: string;
}

export interface RecipeUpdateDto extends RecipeCreateDto {
    id: number;
}

export interface RecipeViewDto {
    id: number;
    title: string;
    description?: string | null;
    isActive: boolean;
}

// Detail (used for edit page)
export interface RecipeDetailDto extends RecipeViewDto {
    categoryIds: number[];
    ingredients: string[];
    instructionsHtml: string;
}

export const recipeService = {
    async getAll(): Promise<RecipeViewDto[]> {
        const res = await axios.get<RecipeViewDto[]>(RECIPES_URL);
        return res.data;
    },

    async getPaged(page: number, pageSize: number): Promise<PagedResult<RecipeViewDto>> {
        const res = await axios.get<PagedResult<RecipeViewDto>>(RECIPES_URL, {
            params: { page, pageSize },
        });
        return res.data;
    },

    async getById(id: number): Promise<RecipeDetailDto> {
        const res = await axios.get<RecipeDetailDto>(`${RECIPES_URL}/${id}`);
        return res.data;
    },

    async create(payload: RecipeCreateDto): Promise<RecipeViewDto> {
        const res = await axios.post<RecipeViewDto>(RECIPES_URL, payload);
        return res.data;
    },

    async update(payload: RecipeUpdateDto): Promise<RecipeViewDto> {
        const res = await axios.put<RecipeViewDto>(`${RECIPES_URL}/${payload.id}`, payload);
        return res.data;
    },

    async delete(id: number): Promise<void> {
        await axios.delete(`${RECIPES_URL}/${id}`);
    },

    async restore(id: number): Promise<void> {
        await axios.post(`${RECIPES_URL}/${id}/restore`);
    },

    async hardDelete(id: number): Promise<void> {
        await axios.delete(`${RECIPES_URL}/${id}/hard`);
    },

    async uploadImage(recipeId: number, file: File) {
        const fd = new FormData();
        fd.append("image", file);

        const res = await fetch(`${RECIPES_URL}/${recipeId}/image`, {
            method: "POST",
            body: fd,
            // IMPORTANT: don't set Content-Type manually for FormData
            credentials: "include", // if you use cookies/auth; remove if not needed
        });

        if (!res.ok) {
            const text = await res.text();
            throw new Error(text || "Failed to upload image.");
        }
    },

    async getImageUrl(recipeId: number): Promise<string> {
        const res = await fetch(`${RECIPES_URL}/${recipeId}/image-url`, {
            method: "GET",
            credentials: "include",
        });

        if (!res.ok) {
            // If no image, your backend returns 404 � treat as "no image"
            if (res.status === 404) return "";
            const text = await res.text();
            throw new Error(text || "Failed to load image url.");
        }

        const data = await res.json();
        return data.url ?? "";
    },
};
